import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../context/ThemeContext";

const SunIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

const MoonIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

function Nav({ Dark }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { dark, toggleTheme } = useTheme();

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <Section>
        <Container>
          <Links>
            <Logo to="/" onClick={closeMenu}>
              savad
            </Logo>
            <List style={{ gap: "20px" }}>
              <ListItemWrapper>
                <ListItem>
                  <ListAnchor to="/about-me">
                    <Span>About</Span>
                  </ListAnchor>
                </ListItem>
              </ListItemWrapper>
              <ListItemWrapper>
                <ListItem>
                  <ListAnchor to="/work">
                    <Span>Work</Span>
                  </ListAnchor>
                </ListItem>
              </ListItemWrapper>
              <ListItemWrapper>
                <ListItem>
                  <ListAnchor to="/contact">
                    <Span>Contact</Span>
                  </ListAnchor>
                </ListItem>
              </ListItemWrapper>
            </List>

            <NavRight>
              <ToggleBtn
                onClick={toggleTheme}
                aria-label={
                  dark ? "Switch to light mode" : "Switch to dark mode"
                }
                title={dark ? "Light mode" : "Dark mode"}
              >
                {dark ? <SunIcon /> : <MoonIcon />}
              </ToggleBtn>
              <Hamburger
                onClick={() => setMenuOpen((o) => !o)}
                aria-label="Toggle menu"
              >
                <span className={menuOpen ? "open" : ""} />
                <span className={menuOpen ? "open" : ""} />
                <span className={menuOpen ? "open" : ""} />
              </Hamburger>
            </NavRight>
          </Links>
        </Container>
      </Section>

      <AnimatePresence>
        {menuOpen && (
          <MobileMenu
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.25 }}
          >
            <MobileLink to="/" onClick={closeMenu}>
              Home
            </MobileLink>
            <MobileLink to="/about-me" onClick={closeMenu}>
              About
            </MobileLink>
            <MobileLink to="/work" onClick={closeMenu}>
              Work
            </MobileLink>
            <MobileLink to="/contact" onClick={closeMenu}>
              Contact
            </MobileLink>
            <MobileThemeRow>
              <MobileThemeLabel>
                {dark ? "Dark mode" : "Light mode"}
              </MobileThemeLabel>
              <MobileToggle
                onClick={() => {
                  toggleTheme();
                  closeMenu();
                }}
              >
                {dark ? <SunIcon /> : <MoonIcon />}
              </MobileToggle>
            </MobileThemeRow>
          </MobileMenu>
        )}
      </AnimatePresence>
    </>
  );
}

export default Nav;

const Section = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  background-color: var(--nav-bg);
  border-bottom: 1px solid var(--border-sub);
  transition: background-color 0.3s ease;
`;

const Container = styled.div`
  width: 1400px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 0;

  @media only screen and (max-width: 1430px) {
    padding: 22px 30px;
    width: 100%;
  }
`;

const Links = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 50px;
`;

const Logo = styled(Link)`
  color: var(--text);
  font-size: 18px;
  font-weight: 700;
  text-transform: uppercase;
  text-decoration: none;
  letter-spacing: 2px;
  transition: opacity 0.2s ease;
  flex-shrink: 0;
  &:hover {
    opacity: 0.55;
  }
`;

const List = styled.ul`
  display: flex;
  align-items: center;
  flex: 1;
  justify-content: flex-end;

  @media only screen and (max-width: 768px) {
    display: none;
  }
`;

const ListItemWrapper = styled.div`
  position: relative;
  overflow-y: hidden;
  padding: 5px 20px;
  z-index: 0;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    transition: 0.5s linear;
    height: 100%;
    display: block;
    background-color: var(--text);
    height: 25px;
    animation: slide 1s linear;
  }
`;

const ListItem = styled.li`
  list-style-type: none;
  color: white;
  cursor: pointer;
  animation: slide-up 2s cubic-bezier(0.65, 0, 0.35, 1) both;
`;

const ListAnchor = styled(Link)`
  text-decoration: none;
  color: var(--text-2);
  font-size: 16px;
  font-weight: 400;
  letter-spacing: 0.5px;
  transition: color 0.3s ease;
  display: block;
  position: relative;
  &:hover {
    color: var(--text);
  }
  &::before {
    content: "";
    position: absolute;
    width: 0%;
    background: var(--text);
    height: 1px;
    bottom: -2px;
    left: 0;
    display: block;
    transition: width 0.35s ease;
  }
  &:hover::before {
    width: 100%;
  }
`;

const Span = styled.span`
  overflow-y: hidden;
`;

const NavRight = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
`;

const ToggleBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--btn-bg);
  border: 1px solid var(--border);
  cursor: pointer;
  transition: background 0.3s ease, border-color 0.3s ease, transform 0.2s ease;
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
    color: var(--text);
    transition: color 0.3s ease;
    flex-shrink: 0;
  }

  &:hover {
    background: var(--text);
    border-color: var(--text);
    transform: rotate(20deg);
    svg {
      color: var(--bg);
    }
  }
`;

const Hamburger = styled.button`
  display: none;
  flex-direction: column;
  justify-content: space-between;
  width: 24px;
  height: 18px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 101;

  span {
    display: block;
    width: 100%;
    height: 2px;
    background: var(--text);
    border-radius: 2px;
    transition: transform 0.3s ease, opacity 0.3s ease;
    transform-origin: center;
  }

  span.open:nth-child(1) {
    transform: translateY(8px) rotate(45deg);
  }
  span.open:nth-child(2) {
    opacity: 0;
  }
  span.open:nth-child(3) {
    transform: translateY(-8px) rotate(-45deg);
  }

  @media only screen and (max-width: 768px) {
    display: flex;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 65px;
  left: 0;
  right: 0;
  background-color: var(--nav-bg);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid var(--border-sub);
  z-index: 99;
  display: flex;
  flex-direction: column;
  padding: 24px 30px 32px;
  gap: 4px;
`;

const MobileLink = styled(Link)`
  text-decoration: none;
  color: var(--text);
  font-size: 28px;
  font-weight: 600;
  letter-spacing: -0.5px;
  padding: 12px 0;
  border-bottom: 1px solid var(--border-sub);
  transition: opacity 0.2s ease;
  &:last-of-type {
    border-bottom: none;
  }
  &:hover {
    opacity: 0.5;
  }
`;

const MobileThemeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 0 0;
  border-top: 1px solid var(--border-sub);
  margin-top: 8px;
`;

const MobileThemeLabel = styled.span`
  font-size: 14px;
  color: var(--text-5);
  letter-spacing: 0.5px;
`;

const MobileToggle = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--btn-bg);
  border: 1px solid var(--border);
  cursor: pointer;

  svg {
    width: 18px;
    height: 18px;
    color: var(--text);
  }
`;
